import React, { useState, useEffect, useCallback } from "react";
import { apiRequests } from "../../shared/api/api";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Label } from "src/components/ui/label";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { Badge } from "src/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Skeleton } from "src/components/ui/skeleton";
import { toast } from "sonner";

import {
  Plus,
  QrCode,
  BarChart3,
  Trash2,
  Pencil,
  Eye,
  RotateCcw,
  Users,
  CheckCircle2,
  XCircle,
  Copy,
  TrendingUp,
  MousePointerClick,
  UserCheck,
  Percent,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "src/components/ui/chart";

import {
  BarChart,
  LineChart,
  PieChart,
  Bar,
  Line,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

const chartConfig = {
  visits: { label: "Переходы", color: "hsl(217, 91%, 60%)" },
  registrations: { label: "Регистрации", color: "hsl(221, 83%, 53%)" },
  clicks: { label: "Клики", color: "hsl(213, 97%, 87%)" },
  conversion: { label: "CR%", color: "hsl(224, 76%, 48%)" },
};

const compareChartConfig = {
  Переходы: { label: "Переходы", color: "hsl(217, 91%, 60%)" },
  Регистрации: { label: "Регистрации", color: "hsl(221, 83%, 53%)" },
  Клики: { label: "Клики", color: "hsl(213, 97%, 87%)" },
};

const C_BTN = {
  Max: "#1677ff",
  Telegram: "#3b82f6",
  SMS: "#60a5fa",
  Other: "#93c5fd",
};

const blueShades = [
  "hsl(217,91%,60%)",
  "hsl(221,83%,53%)",
  "hsl(213,97%,87%)",
  "hsl(224,76%,48%)",
  "hsl(210,98%,78%)",
  "hsl(199,89%,48%)",
];

const QR_DOMAIN = process.env.REACT_APP_QR_DOMAIN || "https://qrrun.ru";
const QR_SRC = (code, size = 100) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    `${QR_DOMAIN}/qr/${code}`,
  )}`;

const InlineLanding = ({ page }) => {
  if (!page) return null;
  const isSVG = (s) => typeof s === "string" && s.trim().startsWith("<svg");

  const visibleButtons = (page.buttons || []).filter(
    (btn) => btn.type === "SMS" || (btn.bot_url && btn.bot_url.trim() !== ""),
  );

  const getBtnStyle = (type) => {
    if (type === "Telegram")
      return {
        background: "#fff",
        color: "#111",
        border: "1px solid #d1d5db",
        fontWeight: 400,
      };
    return { background: "#111", color: "#fff", fontWeight: 400 };
  };

  return (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        background: "#f5f5f5",
        borderRadius: 16,
        overflow: "hidden",
        maxWidth: 320,
        margin: "0 auto",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      }}
    >
      {/* Cover image */}
      {page.image_url ? (
        isSVG(page.image_url) ? (
          <div
            dangerouslySetInnerHTML={{ __html: page.image_url }}
            style={{ height: 160, width: "100%", overflow: "hidden" }}
          />
        ) : (
          <img
            src={page.image_url}
            alt="cover"
            style={{
              height: 160,
              width: "100%",
              objectFit: "contain",
              background: "#e5e7eb",
            }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        )
      ) : (
        <div style={{ height: 160, background: "#e5e7eb" }} />
      )}

      {/* Content */}
      <div style={{ padding: "16px 16px 20px", background: "#fff" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          {page.logo &&
            (isSVG(page.logo) ? (
              <div
                dangerouslySetInnerHTML={{ __html: page.logo }}
                style={{ height: 24, width: 24 }}
              />
            ) : (
              <span style={{ fontSize: 24 }}>{page.logo}</span>
            ))}
          <span style={{ fontSize: 18, fontWeight: 600, color: "#111" }}>
            {page.name}
          </span>
        </div>
        {page.description && (
          <p
            style={{
              textAlign: "center",
              fontSize: 13,
              color: "#6b7280",
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            {page.description}
          </p>
        )}

        {/* Buttons */}
        {visibleButtons.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {visibleButtons.map((btn) => (
              <button
                key={btn.type}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                  fontSize: 14,
                  transition: "opacity .15s",
                  ...getBtnStyle(btn.type),
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        )}

        {/* Tags */}
        {page.auto_tags?.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              justifyContent: "center",
            }}
          >
            {page.auto_tags.map((tag) => (
              <span
                key={tag}
                style={{
                  background: "#f3f4f6",
                  color: "#6b7280",
                  borderRadius: 9999,
                  padding: "2px 8px",
                  fontSize: 11,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Preview Modal ────────────────────────────────────────────────────────────

const PreviewModal = ({ open, onOpenChange, page }) => {
  if (!page) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" style={{ zIndex: 2000 }}>
        <DialogHeader>
          <DialogTitle className="text-base">Превью мини-лендинга</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <InlineLanding page={page}/>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QRCodeModal = ({ open, onOpenChange, page }) => {
  if (!page) return null;
  const landing = `${QR_DOMAIN}/qr/${page.short_code}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" style={{ zIndex: 1005 }}>
        <DialogHeader>
          <DialogTitle className="text-base">QR-код кампании</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <img
            src={QR_SRC(page.short_code, 200)}
            alt="QR"
            className="w-48 h-48"
          />
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span className="truncate max-w-[220px]">{landing}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(landing);
                toast.success("Ссылка скопирована");
              }}
              className="hover:text-foreground transition-colors"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            UTM: {page.utm_source} / {page.utm_medium} / {page.utm_campaign}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VisitsModal = ({ open, onOpenChange, page, visits, loading }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[80vh] overflow-auto z-500"
        style={{ zIndex: 2000 }}
      >
        <DialogHeader>
          <DialogTitle className="text-base">
            {page?.name} — история переходов
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : visits.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            Нет переходов по QR-коду
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата/время</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Кнопка</TableHead>
                <TableHead>Цель</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visits.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="text-sm">
                    {v.created_at
                      ? new Date(v.created_at).toLocaleString("ru-RU")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {v.ip || "—"}
                  </TableCell>
                  <TableCell>
                    {v.button_type ? (
                      <Badge
                        variant="outline"
                        style={{
                          borderColor: C_BTN[v.button_type],
                          color: C_BTN[v.button_type],
                        }}
                      >
                        {v.button_type}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {v.target_reached ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200 border gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Регистрация
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Нет
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Campaign Form Modal ───────────────────────────────────────────────────────

const CampaignFormModal = ({ open, onOpenChange, onSave, initial }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [collectUtm, setCollectUtm] = useState(true);
  const [activeFormTab, setActiveFormTab] = useState("basic");

  useEffect(() => {
    if (open) {
      const v = initial || {};
      const maxBtn = (v.buttons || []).find((b) => b.type === "Max");
      const tgBtn = (v.buttons || []).find((b) => b.type === "Telegram");
      setFormData({
        source_name: v.source_name || "",
        name: v.name || "",
        description: v.description || "",
        image_url: v.image_url || "",
        logo: v.logo || "",
        utm_source: v.utm_source || "qr_cashier",
        utm_medium: v.utm_medium || "qr_physical",
        utm_campaign: v.utm_campaign || "loyalty_card",
        collect_form_utm: v.collect_form_utm !== false,
        auto_tags: (v.auto_tags || []).join(", "),
        status: v.status || "active",
        max_bot_url: maxBtn?.bot_url || "",
        telegram_bot_url: tgBtn?.bot_url || "",
      });
      setCollectUtm(v.collect_form_utm !== false);
    }
  }, [open, initial]);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const buttons = [];
      if (formData.max_bot_url?.trim()) {
        buttons.push({
          type: "Max",
          label: "Через Max",
          bot_url: formData.max_bot_url.trim(),
        });
      }
      if (formData.telegram_bot_url?.trim()) {
        buttons.push({
          type: "Telegram",
          label: "Telegram",
          bot_url: formData.telegram_bot_url.trim(),
        });
      }
      buttons.push({ type: "SMS", label: "Через SMS" });

      await onSave({
        source_name: formData.source_name,
        name: formData.name,
        description: formData.description,
        image_url: formData.image_url,
        logo: formData.logo,
        utm_source: formData.utm_source,
        utm_medium: formData.utm_medium,
        utm_campaign: formData.utm_campaign,
        collect_form_utm: formData.collect_form_utm,
        auto_tags: formData.auto_tags
          ? formData.auto_tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
        status: formData.status,
        buttons,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} style={{ zIndex: 1000 }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto z-[3000]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {initial ? "Редактировать кампанию" : "Новая кампания"}
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeFormTab}
          onValueChange={setActiveFormTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="basic">Основное</TabsTrigger>
            <TabsTrigger value="buttons-utm">Кнопки и UTM</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit} className="space-y-4">
            <TabsContent value="basic" className="space-y-4 mt-0">
              <div className="space-y-1.5">
                <Label htmlFor="source_name" className="text-sm">
                  Назовите данный источник
                </Label>
                <Input
                  id="source_name"
                  value={formData.source_name}
                  onChange={(e) => handleChange("source_name", e.target.value)}
                  placeholder="Касса №1 — основная"
                />
                <p className="text-xs text-muted-foreground">
                  Например: «Касса №3», «Витрина входная», «Листовка у стойки»
                </p>
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="name"
                  className="text-sm after:content-['*'] after:ml-0.5 after:text-red-500"
                >
                  Название кампании
                </Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Premium Кешбэк 2025"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="description" className="text-sm">
                  Описание
                </Label>
                <Textarea
                  id="description"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Сканируй QR на кассе → получи баллы и выбери удобный канал"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="image_url" className="text-sm">
                    Изображение (URL или SVG)
                  </Label>
                  <Textarea
                    id="image_url"
                    rows={3}
                    value={formData.image_url}
                    onChange={(e) => handleChange("image_url", e.target.value)}
                    placeholder={"https://... или\n<svg xmlns=...>...</svg>"}
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="logo" className="text-sm">
                    Логотип (эмодзи, текст или SVG)
                  </Label>
                  <Textarea
                    id="logo"
                    rows={3}
                    value={formData.logo}
                    onChange={(e) => handleChange("logo", e.target.value)}
                    placeholder={"💎 PremiumClub\n\nили <svg>...</svg>"}
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="auto_tags" className="text-sm">
                  Автоматические теги (через запятую)
                </Label>
                <Input
                  id="auto_tags"
                  value={formData.auto_tags}
                  onChange={(e) => handleChange("auto_tags", e.target.value)}
                  placeholder="offline, cashier, loyalty, summer2025"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Статус</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    <SelectItem value="active">Активна</SelectItem>
                    <SelectItem value="draft">Черновик</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="buttons-utm" className="space-y-4 mt-0">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium mb-2">Кнопки лендинга</p>
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="max_bot_url"
                    className="text-sm flex items-center gap-2"
                  >
                    Max — ссылка на бота
                  </Label>
                  <Input
                    id="max_bot_url"
                    value={formData.max_bot_url}
                    onChange={(e) =>
                      handleChange("max_bot_url", e.target.value)
                    }
                    className="text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Оставьте пустым — кнопка не будет показана
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="telegram_bot_url"
                    className="text-sm flex items-center gap-2"
                  >
                    Telegram — ссылка на бота
                  </Label>
                  <Input
                    id="telegram_bot_url"
                    value={formData.telegram_bot_url}
                    onChange={(e) =>
                      handleChange("telegram_bot_url", e.target.value)
                    }
                    className="text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Оставьте пустым — кнопка не будет показана
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {["utm_source", "utm_medium", "utm_campaign"].map((field) => (
                  <div key={field} className="space-y-1.5">
                    <Label htmlFor={field} className="text-sm">
                      {field}
                    </Label>
                    <Input
                      id={field}
                      value={formData[field]}
                      onChange={(e) => handleChange(field, e.target.value)}
                      placeholder={
                        field === "utm_source"
                          ? "qr_cashier"
                          : field === "utm_medium"
                            ? "qr_physical"
                            : "loyalty_card"
                      }
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="collect_utm"
                  checked={collectUtm}
                  onCheckedChange={(checked) => {
                    setCollectUtm(checked);
                    handleChange("collect_form_utm", checked);
                  }}
                />
                <Label
                  htmlFor="collect_utm"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Собирать UTM из адресной строки при переходе
                </Label>
              </div>
            </TabsContent>

            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Отмена
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const CampaignsDataTable = ({
  pages,
  loading,
  onEdit,
  onDelete,
  onPreview,
  onLog,
  onStats,
  onQrCode,
}) => {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const columns = [
    {
      accessorKey: "source_name",
      header: "Источник / Кампания",
      cell: ({ row }) => {
        const page = row.original;
        return (
          <div className="space-y-0.5 max-w-xs mx-auto">
            <p className="text-sm font-medium text-neutral-900">
              {page.source_name || page.name}
            </p>
            {page.source_name && (
              <p className="text-xs text-neutral-500">{page.name}</p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "stats.visits",
      header: "Переходов",
      cell: ({ row }) => (
        <div className="text-center font-medium tabular-nums">
          {row.original.stats?.visits ?? 0}
        </div>
      ),
    },
    {
      accessorKey: "stats.clicks",
      header: "Кликов",
      cell: ({ row }) => (
        <div className="text-center font-medium tabular-nums">
          {row.original.stats?.clicks ?? 0}
        </div>
      ),
    },
    {
      accessorKey: "stats.registrations",
      header: "Регистраций",
      cell: ({ row }) => (
        <div className="text-center font-medium tabular-nums">
          {row.original.stats?.registrations ?? 0}
        </div>
      ),
    },
    {
      accessorKey: "stats.conversion",
      header: "CR%",
      cell: ({ row }) => (
        <div className="text-center font-medium tabular-nums">
          {row.original.stats?.conversion ?? 0}%
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Создана",
      cell: ({ row }) => (
        <div className="text-center text-sm text-neutral-500">
          {row.original.created_at
            ? new Date(row.original.created_at).toLocaleDateString("ru-RU")
            : "—"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Статус",
      cell: ({ row }) => (
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-neutral-100 px-3 py-1 text-xs font-medium">
            {row.original.status === "active" ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-neutral-500" />
            )}
            <span className="text-neutral-700">
              {row.original.status === "active" ? "Активна" : "Черновик"}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Действия",
      cell: ({ row }) => {
        const page = row.original;
        return (
          <div className="flex items-center justify-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    onClick={() => onPreview(page)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Превью</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    onClick={() => onQrCode(page)}
                  >
                    <QrCode className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Показать QR</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    onClick={() => onStats(page)}
                  >
                    <BarChart3 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Аналитика</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    onClick={() => onLog(page)}
                  >
                    <Users className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Визиты</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-neutral-900"
                    onClick={() => onEdit(page)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Редактировать</TooltipContent>
              </Tooltip>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-neutral-500 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Удалить кампанию?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Это действие нельзя отменить.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Отмена</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(page.id)}>
                      Удалить
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TooltipProvider>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: pages,
    columns,
    state: { sorting, columnFilters, pagination },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="text-center py-16 text-sm text-neutral-500">
        Нет кампаний. Создайте первую.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-neutral-200 overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-xs text-center cursor-pointer select-none text-black font-medium"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {{
                      asc: " 🔼",
                      desc: " 🔽",
                    }[header.column.getIsSorted()] ?? null}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="border-b border-neutral-200">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="text-center py-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end px-2">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Строк на странице:</span>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 40, 50].map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-neutral-600">
            Страница {table.getState().pagination.pageIndex + 1} из{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
// ─── Analytics Tab ────────────────────────────────────────────────────────────

const SummaryStatCard = ({ label, value, icon: Icon, colorClass, trend }) => (
  <Card>
    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-xs font-medium text-muted-foreground">
        {label}
      </CardTitle>
      <Icon className={`h-4 w-4 ${colorClass}`} />
    </CardHeader>
    <CardContent className="pb-3">
      <p className={`text-2xl font-bold tabular-nums ${colorClass}`}>{value}</p>
      {trend && (
        <p className="text-[11px] text-muted-foreground mt-0.5">{trend}</p>
      )}
    </CardContent>
  </Card>
);

const AnalyticsTab = ({ pages, selId, onSel, stats, statsLoading }) => {
  const barData = pages.map((p) => ({
    name: (p.source_name || p.name).slice(0, 14),
    Переходы: p.stats?.visits || 0,
    Регистрации: p.stats?.registrations || 0,
    Клики: p.stats?.clicks || 0,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 h-full items-stretch">
      {/* LEFT — фиксированная высота за счёт flex колонки */}
      <div className="lg:col-span-2 flex flex-col gap-4 h-full">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Детальная аналитика</CardTitle>
            <CardDescription className="text-xs">
              Выберите кампанию для анализа
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              value={selId?.toString() || ""}
              onValueChange={(v) => onSel(v ? Number(v) : null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите кампанию" />
              </SelectTrigger>
              <SelectContent position="popper">
                {pages.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.source_name ? `${p.source_name} — ` : ""}
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {statsLoading && (
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            )}

            {!selId && !statsLoading && (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Выберите кампанию для просмотра статистики
              </div>
            )}

            {selId && stats?.summary && !statsLoading && (
              <div className="grid grid-cols-2 gap-3">
                <SummaryStatCard
                  label="Переходы"
                  value={stats.summary.visits}
                  icon={MousePointerClick}
                  colorClass="text-blue-500"
                  trend="Всего сканирований QR"
                />
                <SummaryStatCard
                  label="Регистрации"
                  value={stats.summary.registrations}
                  icon={UserCheck}
                  colorClass="text-blue-600"
                  trend="Успешные регистрации"
                />
                <SummaryStatCard
                  label="Клики"
                  value={stats.summary.clicks}
                  icon={BarChart3}
                  colorClass="text-blue-400"
                  trend="Нажатий на кнопки"
                />
                <SummaryStatCard
                  label="CR%"
                  value={`${stats.summary.conversion}%`}
                  icon={Percent}
                  colorClass="text-blue-800"
                  trend="Конверсия в регистрацию"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Сравнение кампаний</CardTitle>
            <CardDescription className="text-xs">
              Все активные источники
            </CardDescription>
          </CardHeader>
          <CardContent>
            {barData.length ? (
              <ChartContainer
                config={compareChartConfig}
                className="h-[220px] w-full"
              >
                <BarChart
                  data={barData}
                  margin={{ left: -12, right: 4, top: 4, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 9 }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={4}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar
                    dataKey="Переходы"
                    fill="var(--color-Переходы)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Регистрации"
                    fill="var(--color-Регистрации)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Клики"
                    fill="var(--color-Клики)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="text-center py-10 text-sm text-muted-foreground">
                Нет данных
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* RIGHT — растягивается на всю высоту левой колонки */}
      <div className="lg:col-span-3 flex flex-col h-full">
        {!selId && !statsLoading && (
          <div className="flex items-center justify-center h-full min-h-[300px] rounded-lg border border-dashed text-sm text-muted-foreground">
            Выберите кампанию слева для просмотра графиков
          </div>
        )}
        {statsLoading && (
          <div className="space-y-4 flex-1">
            <Skeleton className="h-64 w-full" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        )}
        {selId && stats && !statsLoading && (
          <div className="flex-1 flex flex-col">
            <DetailCharts stats={stats} />
          </div>
        )}
      </div>
    </div>
  );
};

// DetailCharts — тоже добавляем flex-1, чтобы растягивался
const DetailCharts = ({ stats }) => {
  if (!stats) return <Skeleton className="h-40 w-full" />;
  const { daily = [], buttons = [] } = stats;

  return (
    <div className="flex flex-col gap-4 h-full">
      <Card className="flex-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Динамика переходов и регистраций
          </CardTitle>
          <CardDescription className="text-xs">
            За последние 7 дней
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[200px] w-full">
            <BarChart
              data={daily}
              margin={{ left: -12, right: 4, top: 4, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                className="stroke-muted"
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickMargin={4}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="visits"
                fill="var(--color-visits)"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="registrations"
                fill="var(--color-registrations)"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="clicks"
                fill="var(--color-clicks)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        </CardContent>
        <CardFooter className="pt-0 pb-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
            Динамика за 7 дней
          </div>
        </CardFooter>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Конверсия CR%</CardTitle>
            <CardDescription className="text-xs">
              Ежедневная конверсия
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[180px] w-full">
              <LineChart
                data={daily}
                margin={{ left: -12, right: 4, top: 4, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  className="stroke-muted"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                />
                <ChartTooltip
                  content={<ChartTooltipContent formatter={(v) => `${v}%`} />}
                />
                <Line
                  type="monotone"
                  dataKey="conversion"
                  stroke="var(--color-conversion)"
                  strokeWidth={2.5}
                  dot={{
                    r: 3,
                    strokeWidth: 0,
                    fill: "var(--color-conversion)",
                  }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {buttons.length > 0 ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Распределение по кнопкам
              </CardTitle>
              <CardDescription className="text-xs">
                Клики по каналам
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <PieChart>
                  <Pie
                    data={buttons}
                    dataKey="count"
                    nameKey="button_type"
                    cx="50%"
                    cy="50%"
                    outerRadius={65}
                    innerRadius={30}
                    label={({ button_type, count }) =>
                      `${button_type}: ${count}`
                    }
                    labelLine={false}
                  >
                    {buttons.map((b, idx) => (
                      <Cell
                        key={b.button_type}
                        fill={
                          C_BTN[b.button_type] ||
                          blueShades[idx % blueShades.length]
                        }
                      />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        ) : (
          <Card className="flex items-center justify-center">
            <CardContent className="pt-6 pb-6 text-center text-sm text-muted-foreground">
              Нет данных по кнопкам
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ─── Root Component ───────────────────────────────────────────────────────────

export default function LoyaltyQRPage({ token, websocket, initialData }) {
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("campaigns");

  const [formOpen, setFormOpen] = useState(false);
  const [formInit, setFormInit] = useState(null);

  const [previewPage, setPreviewPage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [visitsPage, setVisitsPage] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const [selPageId, setSelPageId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalPage, setQrModalPage] = useState(null);

  useEffect(() => {
    apiRequests.setToken(token);
  }, [token]);

  const reload = useCallback(async () => {
    setPagesLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getPages();
      const data = response.data;
      setPages(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Ошибка загрузки кампаний");
    } finally {
      setPagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) reload();
  }, [token, reload]);

  const handleSave = useCallback(
    async (vals) => {
      if (formInit?.id) {
        await apiRequests.loyaltyQr.updatePage(formInit.id, vals);
        toast.success("Обновлено");
      } else {
        await apiRequests.loyaltyQr.createPage(vals);
        toast.success("Кампания создана");
      }
      setFormOpen(false);
      setFormInit(null);
      reload();
    },
    [formInit, reload],
  );

  const handleDelete = useCallback(
    async (id) => {
      await apiRequests.loyaltyQr.deletePage(id);
      toast.success("Удалено");
      reload();
    },
    [reload],
  );

  const handleLog = useCallback(async (page) => {
    setVisitsPage(page);
    setVisitsOpen(true);
    setVisitsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getVisits(page.id);
      const data = response.data;
      setVisits(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Ошибка загрузки визитов");
    } finally {
      setVisitsLoading(false);
    }
  }, []);

  const handleSelPage = useCallback(async (id) => {
    setSelPageId(id);
    if (!id) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getStats(id, 7);
      const data = response.data;
      setStats(data);
    } catch {
      toast.error("Ошибка загрузки статистики");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleStatsBtn = useCallback(
    (page) => {
      setActiveTab("analytics");
      handleSelPage(page.id);
    },
    [handleSelPage],
  );

  return (
    <div className="pt-4 space-y-5">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between gap-1 mb-5">
          <TabsList>
            <TabsTrigger
              value="campaigns"
              className="gap-1.5 text-sm h-10 px-5"
            >
              <QrCode className="h-4 w-4" />
              Кампании
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="gap-1.5 text-sm h-10 px-5"
            >
              <BarChart3 className="h-4 w-4" />
              Аналитика
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={reload}
              disabled={pagesLoading}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Обновить
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setFormInit(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Новая кампания
            </Button>
          </div>
        </div>

        <TabsContent value="campaigns">
          <CampaignsDataTable
            pages={pages}
            loading={pagesLoading}
            onEdit={(r) => {
              setFormInit(r);
              setFormOpen(true);
            }}
            onDelete={handleDelete}
            onPreview={(r) => {
              setPreviewPage(r);
              setPreviewOpen(true);
            }}
            onQrCode={(r) => {
              setQrModalPage(r);
              setQrModalOpen(true);
            }}
            onLog={handleLog}
            onStats={handleStatsBtn}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab
            pages={pages}
            selId={selPageId}
            onSel={handleSelPage}
            stats={stats}
            statsLoading={statsLoading}
          />
        </TabsContent>
      </Tabs>

      <CampaignFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        onSave={handleSave}
        initial={formInit}
      />

      <PreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        page={previewPage}
      />

      <VisitsModal
        open={visitsOpen}
        onOpenChange={setVisitsOpen}
        page={visitsPage}
        visits={visits}
        loading={visitsLoading}
      />
      <QRCodeModal
        open={qrModalOpen}
        onOpenChange={setQrModalOpen}
        page={qrModalPage}
      />
    </div>
  );
}
